import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import {
  ArrowPathIcon,
  ArrowUpTrayIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  UserGroupIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';

const ANALYSIS_TYPES = [
  { value: 'hitting', label: 'Hitting', icon: '⚾' },
  { value: 'pitching', label: 'Pitching', icon: '🥎' },
  { value: 'fielding', label: 'Fielding', icon: '🥅' },
];

const TONE_OPTIONS = [
  { value: 'encouraging', label: 'Encouraging', icon: '🌟' },
  { value: 'realistic', label: 'Realistic', icon: '📋' },
  { value: 'grumpy', label: 'Grumpy Coach', icon: '😤' },
  { value: 'roast', label: 'Roast Me', icon: '🔥' },
];

const ANALYSIS_STEPS = [
  'Upload received',
  'Extracting frames',
  'Evaluating movement',
  'Building coaching summary',
];

const MAX_VIDEO_SIZE_BYTES = 100 * 1024 * 1024;
const UPLOAD_TIMEOUT_MS = 130000;

const clampScore = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 0;
  return Math.max(0, Math.min(100, numericValue));
};

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return [];
};

const formatRecommendation = (item) => {
  if (!item) return '';
  if (typeof item === 'string') return item.trim();
  if (typeof item === 'object') {
    const drill = item.drill ? String(item.drill).trim() : '';
    const sets = item.sets !== undefined ? String(item.sets).trim() : '';
    const reps = item.reps !== undefined ? String(item.reps).trim() : '';
    const cue = item.cue ? String(item.cue).trim() : '';
    const parts = [];

    if (drill) parts.push(drill);

    const setRep = [sets && `${sets} sets`, reps && `${reps} reps`].filter(Boolean).join(', ');
    if (setRep) parts.push(setRep);
    if (cue) parts.push(`cue: ${cue}`);
    if (parts.length > 0) return parts.join(' - ');
  }
  return '';
};

const normalizeRecommendations = (value) => {
  if (!Array.isArray(value)) return normalizeArray(value);
  return value.map(formatRecommendation).filter(Boolean);
};

const normalizeKeyMoments = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    return [{ timestamp: '', description: value.trim(), importance: 'medium', category: 'note' }];
  }
  return [];
};

const getInitials = (name) =>
  String(name || 'AI')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'AI';

const getAnalysisTypeLabel = (type) =>
  ANALYSIS_TYPES.find((option) => option.value === type)?.label || 'Analysis';

const toSentence = (value) => {
  if (!value || typeof value !== 'string') return '';
  const firstSentence = value
    .split(/(?<=[.!?])\s+/)
    .map((part) => part.trim())
    .find(Boolean);
  return firstSentence || value.trim();
};

const truncateText = (value, length = 140) => {
  if (!value) return '';
  if (value.length <= length) return value;
  return `${value.slice(0, length).trimEnd()}...`;
};

const formatMetricLabel = (key) =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (character) => character.toUpperCase())
    .trim();


const formatDisplayDate = (value) => {
  if (!value) return 'Just now';
  try {
    return new Date(value).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch (error) {
    return 'Just now';
  }
};

const formatSimilarity = (val) => {
  if (val == null) return null;
  const n = typeof val === 'number' ? val : parseInt(String(val).replace('%', ''), 10);
  return Number.isFinite(n) ? n : null;
};

const buildComparisonLine = (comp, analysisType) => {
  if (!comp) return null;
  if (comp.comparison) return comp.comparison;
  const type = getAnalysisTypeLabel(analysisType).toLowerCase();
  return `Your ${type} mechanics remind me of ${comp.player}`;
};

const getComparisonFrameSrc = (frame) => {
  if (!frame) return null;
  if (frame.frameData) return `data:image/png;base64,${frame.frameData}`;
  if (frame.filename) return `/api/video-analysis/comparison-frame/${frame.filename}`;
  return null;
};

const getFocusTone = (score) => {
  if (score >= 85) return { label: 'Locked in', className: 'text-emerald-300' };
  if (score >= 70) return { label: 'Trending up', className: 'text-amber-300' };
  return { label: 'Off target', className: 'text-rose-300' };
};

const buildCoachMessage = (analysis, analysisType) => {
  if (!analysis?.insights) {
    return `Upload a ${getAnalysisTypeLabel(analysisType).toLowerCase()} clip and I'll turn it into one clear coaching plan.`;
  }

  if (analysis.error) {
    return toSentence(analysis.errorMessage) || 'The analysis did not finish. Try a shorter or clearer clip.';
  }

  const improvement = normalizeArray(analysis.insights.improvements)[0];
  const recommendation = normalizeRecommendations(analysis.insights.recommendations)[0];
  const detailedSummary = toSentence(analysis.insights.aiAnalysisText);

  return (
    improvement ||
    recommendation ||
    detailedSummary ||
    `Your ${getAnalysisTypeLabel(analysis.type || analysisType).toLowerCase()} clip is ready with a focused practice plan.`
  );
};

const buildPracticeFocus = (analysis, analysisType) => {
  const fallbackTitle = `${getAnalysisTypeLabel(analysisType)} focus`;
  if (!analysis?.insights) {
    return {
      title: fallbackTitle,
      detail: 'Upload a clip to generate a targeted correction and drill sequence.',
    };
  }

  const focusText =
    normalizeArray(analysis.insights.improvements)[0] ||
    normalizeRecommendations(analysis.insights.recommendations)[0] ||
    toSentence(analysis.insights.aiAnalysisText);

  if (!focusText) {
    return {
      title: fallbackTitle,
      detail: 'The analysis is complete, but there was not enough structured detail to build a tighter focus card.',
    };
  }

  const parts = focusText.split(':');
  if (parts.length > 1) {
    return {
      title: parts[0].trim() || fallbackTitle,
      detail: parts.slice(1).join(':').trim() || focusText,
    };
  }

  return {
    title: fallbackTitle,
    detail: focusText,
  };
};

const ScoreRing = ({ score }) => {
  const safeScore = clampScore(score);
  const radius = 48;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - safeScore / 100);

  return (
    <div className="relative h-32 w-32 sm:h-36 sm:w-36">
      <svg className="-rotate-90 h-full w-full" viewBox="0 0 120 120" aria-hidden="true">
        <defs>
          <linearGradient id="analysis-score-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fb7185" />
            <stop offset="52%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#34d399" />
          </linearGradient>
        </defs>
        <circle cx="60" cy="60" r={radius} fill="none" stroke="rgba(148,163,184,0.15)" strokeWidth="12" />
        <circle
          cx="60"
          cy="60"
          r={radius}
          fill="none"
          stroke="url(#analysis-score-gradient)"
          strokeLinecap="round"
          strokeWidth="12"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
        />
      </svg>
      <div className="absolute inset-[18px] flex flex-col items-center justify-center rounded-full border border-white/5 bg-slate-950">
        <span className="text-[10px] font-medium uppercase tracking-[0.28em] text-slate-400">Score</span>
        <span className="mt-1 text-3xl font-semibold text-white">{safeScore}</span>
      </div>
    </div>
  );
};

const VideoAnalysis = () => {
  const { user } = useAuth();
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [analysisType, setAnalysisType] = useState('hitting');
  const [sport, setSport] = useState('baseball');
  const [playerAge, setPlayerAge] = useState('');
  const [tone, setTone] = useState('realistic');
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedFrameIndex, setSelectedFrameIndex] = useState(0);
  const fileInputRef = useRef(null);

  const [showAiConsent, setShowAiConsent] = useState(false);

  // Share with coach
  const [shareNote, setShareNote] = useState('');
  const [isSharing, setIsSharing] = useState(false);
  const [analysisShares, setAnalysisShares] = useState([]);
  const [coachInbox, setCoachInbox] = useState([]);
  const [showCoachInbox, setShowCoachInbox] = useState(false);
  const [respondingTo, setRespondingTo] = useState(null);
  const [coachFeedback, setCoachFeedback] = useState('');
  const [coachDrills, setCoachDrills] = useState('');
  const [isResponding, setIsResponding] = useState(false);

  const score = clampScore(analysis?.insights?.overallScore);
  const coachMessage = useMemo(() => buildCoachMessage(analysis, analysisType), [analysis, analysisType]);
  const practiceFocus = useMemo(() => buildPracticeFocus(analysis, analysisType), [analysis, analysisType]);
  const focusTone = getFocusTone(score);
  const comparisonFrameSrc = getComparisonFrameSrc(analysis?.insights?.comparisonFrame);
  const latestPreviewDate = analysis?.timestamp || selectedVideo?.uploadDate;
  const topMetrics = useMemo(
    () => Object.entries(analysis?.insights?.metrics || {}).slice(0, 4),
    [analysis]
  );
  const analysisNotice = useMemo(() => {
    if (analysis?.fallbackReason === 'api_limit') {
      return {
        tone: 'amber',
        title: 'Demo insights',
        text: 'The AI service hit its current usage limit, so sample insights were shown and the analysis was still saved.',
      };
    }

    if (analysis?.fallbackReason === 'api_error') {
      return {
        tone: 'blue',
        title: 'Demo insights',
        text: 'The AI service was temporarily unavailable, so sample insights were shown and the analysis was still saved.',
      };
    }

    return null;
  }, [analysis]);

  useEffect(() => {
    return () => {
      if (selectedVideo?.previewUrl) {
        URL.revokeObjectURL(selectedVideo.previewUrl);
      }
    };
  }, [selectedVideo]);

  useEffect(() => {
    const fetchOrgSport = async () => {
      try {
        const res = await fetch('/api/organizations/my-organizations', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!res.ok) return;
        const orgs = await res.json();
        const orgList = Array.isArray(orgs) ? orgs : [];
        const firstOrg = orgList[0];
        const orgData = firstOrg?.Organization || firstOrg;
        const orgSport = orgData?.sport;
        if (orgSport === 'softball' || orgSport === 'baseball') {
          setSport(orgSport);
        }
      } catch (_) {
        // fall back to baseball default
      }
    };
    fetchOrgSport();
  }, []);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      toast.error('Please select a valid video file (MP4, MOV, WebM, etc.).');
      return;
    }

    if (file.size > MAX_VIDEO_SIZE_BYTES) {
      toast.error(
        `Video must be under ${MAX_VIDEO_SIZE_BYTES / (1024 * 1024)}MB. Yours is ${(
          file.size /
          (1024 * 1024)
        ).toFixed(1)}MB.`
      );
      return;
    }

    if (file.size === 0) {
      toast.error('The selected file is empty. Please choose another video.');
      return;
    }

    if (selectedVideo?.previewUrl) {
      URL.revokeObjectURL(selectedVideo.previewUrl);
    }

    const videoUrl = URL.createObjectURL(file);
    setSelectedVideo({
      id: Date.now(),
      name: file.name,
      duration: '0:00',
      uploadDate: new Date().toISOString().split('T')[0],
      type: 'upload',
      file,
      previewUrl: videoUrl,
      size: file.size,
    });
  };

  const startAnalysis = async () => {
    if (!selectedVideo?.file) return;

    setIsAnalyzing(true);
    setCurrentStep(0);
    setAnalysis(null);

    // Fake step ticker so the UI feels alive while we wait
    const progressInterval = setInterval(() => {
      setCurrentStep((previous) => Math.min(previous + 1, ANALYSIS_STEPS.length - 1));
    }, 4000);

    const controller = new AbortController();
    const uploadTimeoutId = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);

    try {
      // ── Step 1: upload (returns immediately with videoId) ──────────────────
      const formData = new FormData();
      formData.append('video', selectedVideo.file);
      formData.append('analysisType', analysisType);
      formData.append('sport', sport);
      formData.append('playerInfo', `${user?.name || 'Player'} - ${user?.role || 'Player'}`);
      if (playerAge) formData.append('playerAge', playerAge);
      formData.append('tone', tone);
      formData.append('title', `${analysisType} Analysis - ${selectedVideo.name}`);
      formData.append('description', `AI analysis of ${analysisType} technique`);

      const uploadRes = await fetch('/api/video-analysis/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData,
        signal: controller.signal,
      });

      clearTimeout(uploadTimeoutId);

      if (!uploadRes.ok) {
        const errData = await uploadRes.json().catch(() => ({}));
        throw new Error(errData.details || errData.error || `Upload failed (${uploadRes.status})`);
      }

      const { videoId } = await uploadRes.json();

      // ── Step 2: poll /status/:videoId until terminal state ─────────────────
      const POLL_INTERVAL_MS = 2500;
      const POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes max
      const pollStart = Date.now();

      const result = await new Promise((resolve, reject) => {
        const poll = async () => {
          if (Date.now() - pollStart > POLL_TIMEOUT_MS) {
            return reject(new Error('Analysis timed out after 5 minutes. Try a shorter clip.'));
          }
          try {
            const statusRes = await fetch(`/api/video-analysis/status/${videoId}`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            if (!statusRes.ok) return reject(new Error(`Status check failed (${statusRes.status})`));
            const data = await statusRes.json();

            if (data.status === 'complete') return resolve(data);
            if (data.status === 'failed') return reject(new Error(data.error || 'Analysis failed on server'));

            // Still pending/processing — nudge the step counter and try again
            setCurrentStep((prev) => Math.min(prev + 1, ANALYSIS_STEPS.length - 1));
            setTimeout(poll, POLL_INTERVAL_MS);
          } catch (err) {
            reject(err);
          }
        };
        setTimeout(poll, POLL_INTERVAL_MS);
      });

      clearInterval(progressInterval);
      setCurrentStep(ANALYSIS_STEPS.length);

      const analysisData = result.analysis || {};

      setSelectedFrameIndex(0);
      setAnalysis({
        id: videoId,
        videoId: selectedVideo.id,
        type: analysisType,
        timestamp: new Date(),
        insights: {
          keyMoments: normalizeKeyMoments(analysisData.keyMoments),
          metrics: analysisData.metrics || {},
          recommendations: normalizeRecommendations(analysisData.recommendations),
          professionalComparison: analysisData.professionalComparison || null,
          overallScore: analysisData.overallScore || 0,
          strengths: normalizeArray(analysisData.strengths),
          improvements: normalizeArray(analysisData.improvements),
          aiAnalysisText: analysisData.aiAnalysisText,
          extractedFrames: analysisData.extractedFrames || [],
          comparisonFrame: analysisData.comparisonFrame || null,
          mediaPipeData: analysisData.mediaPipeData || null,
          biomechanics: analysisData.biomechanics || null,
          annotatedFrames: analysisData.annotatedFrames || [],
        },
      });
    } catch (error) {
      clearTimeout(uploadTimeoutId);
      clearInterval(progressInterval);

      const isTimeout = error.name === 'AbortError' || error.message?.toLowerCase().includes('timed out');
      const is503 = error.message?.includes('503');
      let errorMessage = error.message || 'Analysis failed. Please try again.';
      let recommendations = [
        'Use a video under 2 minutes for best results',
        'Ensure the video shows clear baseball technique',
        'Check your connection and try again',
      ];

      if (is503) {
        errorMessage =
          "The request timed out on the server. This often happens when the AI service is over its limit. Try again in a few minutes. When the limit is hit, we'll show demo insights instead of failing.";
        recommendations = [
          'Try again in a few minutes',
          'If you see demo-style results, the AI quota was reached but your analysis was still saved',
          'Use a shorter video (under 2 min) to reduce processing time',
        ];
      } else if (isTimeout) {
        errorMessage = 'Analysis took too long. Try a shorter video (under 2 minutes) or try again.';
      }

      setAnalysis({
        id: Date.now(),
        videoId: selectedVideo?.id,
        type: analysisType,
        sport,
        timestamp: new Date(),
        error: true,
        errorMessage,
        insights: {
          keyMoments: [],
          metrics: {},
          recommendations,
          professionalComparison: null,
          strengths: [],
          improvements: [],
        },
      });
    } finally {
      setIsAnalyzing(false);
      setCurrentStep(ANALYSIS_STEPS.length);
    }
  };

  const resetAnalysis = () => {
    setSelectedVideo(null);
    setAnalysis(null);
    setIsAnalyzing(false);
    setCurrentStep(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const fetchAnalysisHistory = async () => {
    try {
      const response = await fetch('/api/video-analysis/history', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const history = Array.isArray(data) ? data : data.videos || [];
        setAnalysisHistory(history);
      }
    } catch (error) {
      console.error('Failed to fetch analysis history:', error);
    }
  };

  const fetchAnalysisShares = async (videoId) => {
    try {
      const res = await fetch(`/api/video-analysis/${videoId}/shares`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (res.ok) setAnalysisShares(await res.json());
    } catch (e) { /* silent */ }
  };

  const fetchCoachInbox = async () => {
    try {
      const res = await fetch('/api/video-analysis/shares/inbox', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (res.ok) setCoachInbox(await res.json());
    } catch (e) { /* silent */ }
  };

  const handleShareWithCoach = async () => {
    if (!analysis?.id) return;
    setIsSharing(true);
    try {
      const res = await fetch(`/api/video-analysis/${analysis.id}/share`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ player_note: shareNote }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to share');
      toast.success(`Analysis shared with ${data.sharedWith} coach${data.sharedWith !== 1 ? 'es' : ''}!`);
      setShareNote('');
      fetchAnalysisShares(analysis.id);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsSharing(false);
    }
  };

  const handleCoachRespond = async (shareId) => {
    setIsResponding(true);
    try {
      const drillList = coachDrills.split('\n').map(d => d.trim()).filter(Boolean);
      const res = await fetch(`/api/video-analysis/shares/${shareId}/respond`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ coach_feedback: coachFeedback, coach_drills: drillList }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      toast.success('Feedback sent to player!');
      setRespondingTo(null);
      setCoachFeedback('');
      setCoachDrills('');
      fetchCoachInbox();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsResponding(false);
    }
  };

  useEffect(() => {
    fetchAnalysisHistory();
  }, []);

  useEffect(() => {
    if (analysis && !analysis.error) {
      fetchAnalysisHistory();
      fetchAnalysisShares(analysis.id);
    }
  }, [analysis]);

  const isCoach = ['coach', 'assistant_coach', 'admin', 'org_admin'].includes(user?.role);

  useEffect(() => {
    if (isCoach) fetchCoachInbox();
  }, [isCoach]);

  const renderHistory = () => (
    <div className="rounded-[26px] border border-white/10 bg-slate-900/75 p-4 sm:p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.32em] text-slate-400">Recent Analyses</p>
          <h2 className="mt-2 text-lg font-semibold text-white">Saved clips and prior AI reports</h2>
        </div>
        <button
          onClick={() => setShowHistory(false)}
          className="rounded-full border border-white/10 px-3 py-1.5 text-xs font-medium text-slate-300 transition hover:border-white/20 hover:text-white"
        >
          Close
        </button>
      </div>

      {analysisHistory.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 px-4 py-8 text-center text-sm text-slate-400">
          No previous analyses found.
        </div>
      ) : (
        <div className="grid gap-3">
          {analysisHistory.map((historyItem) => {
            const analysisData = historyItem.analysisData || {};
            const savedAnalysis = analysisData.analysis || analysisData;
            const savedType = analysisData.type || historyItem.analysisType || 'analysis';
            const savedScore = clampScore(savedAnalysis.overallScore || analysisData.overallScore);
            const savedFrame = savedAnalysis.comparisonFrame || analysisData.comparisonFrame;
            const savedFrameSrc = getComparisonFrameSrc(savedFrame);

            return (
              <button
                key={historyItem.id}
                type="button"
                onClick={() => {
                  setSelectedFrameIndex(0);
                  setAnalysis({
                    id: historyItem.id,
                    videoId: historyItem.id,
                    type: savedType,
                    timestamp: new Date(historyItem.createdAt),
                    insights: {
                      ...savedAnalysis,
                      comparisonFrame: savedFrame,
                      extractedFrames: savedAnalysis.extractedFrames || analysisData.extractedFrames || [],
                      keyMoments: normalizeKeyMoments(savedAnalysis.keyMoments),
                      metrics: savedAnalysis.metrics || {},
                      recommendations: normalizeRecommendations(savedAnalysis.recommendations),
                      professionalComparison: savedAnalysis.professionalComparison || null,
                      overallScore: savedScore,
                      strengths: normalizeArray(savedAnalysis.strengths),
                      improvements: normalizeArray(savedAnalysis.improvements),
                      aiAnalysisText: savedAnalysis.aiAnalysisText || analysisData.aiAnalysisText,
                      mediaPipeData: savedAnalysis.mediaPipeData || analysisData.mediaPipeData || null,
                      biomechanics: savedAnalysis.biomechanics || analysisData.biomechanics || null,
                      annotatedFrames: savedAnalysis.annotatedFrames || analysisData.annotatedFrames || [],
                    },
                  });
                  setAnalysisType(savedType);
                  setShowHistory(false);
                }}
                className="grid gap-4 rounded-2xl border border-white/10 bg-slate-950/70 p-4 text-left transition hover:border-white/20 hover:bg-slate-950 sm:grid-cols-[112px_minmax(0,1fr)]"
              >
                <div className="overflow-hidden rounded-2xl border border-white/10 bg-slate-900">
                  {savedFrameSrc ? (
                    <img
                      src={savedFrameSrc}
                      alt="Analysis frame"
                      className="h-28 w-full object-cover"
                      onError={(event) => {
                        event.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="flex h-28 items-center justify-center">
                      <VideoCameraIcon className="h-8 w-8 text-slate-600" />
                    </div>
                  )}
                </div>

                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-emerald-400/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-200">
                      {getAnalysisTypeLabel(savedType)}
                    </span>
                    {savedScore > 0 && (
                      <span className="rounded-full bg-white/8 px-2.5 py-1 text-xs font-medium text-white">
                        Score {savedScore}
                      </span>
                    )}
                    <span className="text-xs text-slate-400">{formatDisplayDate(historyItem.createdAt)}</span>
                  </div>

                  <h3 className="mt-3 text-base font-semibold text-white">{historyItem.title}</h3>
                  <p className="mt-1 text-sm text-slate-300">
                    {truncateText(historyItem.description || toSentence(savedAnalysis.aiAnalysisText) || 'Saved AI analysis')}
                  </p>

                  {savedFrame?.timestamp && (
                    <p className="mt-2 text-xs text-slate-500">Frame selected at {savedFrame.timestamp}</p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-full overflow-hidden rounded-[32px] border border-slate-800 bg-slate-950 text-slate-100 shadow-[0_30px_80px_rgba(15,23,42,0.45)]">
      <div className="relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.18),_transparent_32%),radial-gradient(circle_at_bottom_right,_rgba(251,146,60,0.12),_transparent_30%)]" />
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-white/5 to-transparent" />

        <div className="relative p-4 sm:p-6 lg:p-8">
          <div className="flex flex-col gap-4 border-b border-white/10 pb-6 sm:flex-row sm:items-start sm:justify-between">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3">
                <p className="text-xs font-semibold uppercase tracking-[0.36em] text-emerald-300/80">FieldHouse AI</p>
                <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-300">
                  {sport === 'softball' ? '🥎 Softball' : '⚾ Baseball'}
                </span>
              </div>
              <h1 className="mt-3 text-3xl font-semibold tracking-tight text-white sm:text-4xl">
                AI {sport === 'softball' ? 'Motion' : 'Swing'} Analysis
              </h1>
              <p className="mt-3 max-w-xl text-sm leading-6 text-slate-300 sm:text-base">
                A mobile-first coaching view for each clip: one clear summary, one score, one practice focus, and the latest frame or video side by side.
              </p>
            </div>

            <button
              onClick={() => setShowHistory((previous) => !previous)}
              className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-sm font-medium text-slate-200 transition hover:border-white/20 hover:bg-white/10 hover:text-white"
            >
              <ClockIcon className="mr-2 h-4 w-4" />
              History ({analysisHistory.length})
            </button>
          </div>

          {showHistory && <div className="mt-6">{renderHistory()}</div>}

          {analysisNotice && (
            <div
              className={`mt-6 rounded-2xl border px-4 py-3 text-sm ${
                analysisNotice.tone === 'amber'
                  ? 'border-amber-400/25 bg-amber-400/10 text-amber-100'
                  : 'border-sky-400/25 bg-sky-400/10 text-sky-100'
              }`}
            >
              <span className="font-semibold">{analysisNotice.title}:</span> {analysisNotice.text}
            </div>
          )}

          <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(320px,390px)_minmax(0,1fr)]">
            <div className="space-y-6">
              <section className="rounded-[28px] border border-white/10 bg-slate-900/80 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.35)]">
                <div className="flex items-start gap-4">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-300 via-sky-300 to-orange-300 text-base font-semibold text-slate-950">
                    {getInitials(user?.name)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-[0.32em] text-slate-400">AI Coach</p>
                    <div className="mt-3 rounded-[22px] bg-slate-800/95 px-4 py-3 text-[15px] leading-7 text-slate-100 shadow-inner shadow-black/20">
                      {coachMessage}
                    </div>
                  </div>
                </div>
              </section>

              <section className="rounded-[28px] border border-white/10 bg-slate-900/80 p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.32em] text-slate-400">Swing Progress</p>
                <div className="mt-5 grid items-center gap-6 sm:grid-cols-[auto_minmax(0,1fr)]">
                  <ScoreRing score={score} />
                  <div>
                    <p className="text-sm font-medium uppercase tracking-[0.24em] text-slate-400">How to Improve</p>
                    <h2 className="mt-3 text-2xl font-semibold tracking-tight text-white">
                      {practiceFocus.title}
                    </h2>
                    <p className="mt-3 text-sm leading-6 text-slate-300">{practiceFocus.detail}</p>
                    <div className="mt-4 inline-flex items-center rounded-full bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-200">
                      <span className={`mr-2 h-2.5 w-2.5 rounded-full ${score >= 70 ? 'bg-emerald-400' : score >= 50 ? 'bg-amber-400' : 'bg-rose-400'}`} />
                      {analysis ? `${focusTone.label} for this clip` : 'Waiting for a scored clip'}
                    </div>
                  </div>
                </div>
              </section>

              <section className="rounded-[28px] border border-white/10 bg-slate-900/80 p-5">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.32em] text-slate-400">Upload</p>
                    <h2 className="mt-2 text-xl font-semibold text-white">Analyze a new clip</h2>
                  </div>
                  {selectedVideo && (
                    <span className="rounded-full bg-emerald-400/10 px-3 py-1 text-xs font-medium text-emerald-200">
                      {(selectedVideo.size / (1024 * 1024)).toFixed(1)} MB
                    </span>
                  )}
                </div>

                <div className="mt-5 rounded-[24px] border border-dashed border-white/15 bg-slate-950/70 p-5 text-center">
                  <ArrowUpTrayIcon className="mx-auto h-10 w-10 text-slate-500" />
                  <p className="mt-4 text-sm font-medium text-white">Upload a video file for analysis</p>
                  <p className="mt-1 text-sm text-slate-400">Best results come from a clear side view under two minutes.</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-4 inline-flex items-center rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-slate-200"
                  >
                    Choose File
                  </button>
                </div>

                {selectedVideo && (
                  <div className="mt-4 rounded-2xl border border-cyan-400/20 bg-cyan-400/10 px-4 py-3">
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="font-medium text-cyan-100">Ready for analysis</span>
                      <span className="text-cyan-200">{selectedVideo.duration}</span>
                    </div>
                    <p className="mt-1 truncate text-sm text-cyan-50">{selectedVideo.name}</p>
                  </div>
                )}

                <div className="mt-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-400">Analysis Type</p>
                  <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {ANALYSIS_TYPES.map((option) => {
                      const active = analysisType === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setAnalysisType(option.value)}
                          className={`rounded-2xl border px-4 py-3 text-left transition ${
                            active
                              ? 'border-emerald-300/40 bg-emerald-300/10 text-white'
                              : 'border-white/10 bg-white/5 text-slate-300 hover:border-white/20 hover:text-white'
                          }`}
                        >
                          <div className="text-lg">{option.icon}</div>
                          <div className="mt-2 text-sm font-medium">{option.label}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-5">
                  <label className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-400" htmlFor="player-age">
                    Player Age
                  </label>
                  <input
                    id="player-age"
                    type="number"
                    min="5"
                    max="30"
                    value={playerAge}
                    onChange={(event) => setPlayerAge(event.target.value)}
                    placeholder="Optional"
                    className="mt-3 w-full rounded-2xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-emerald-300/40"
                  />
                  <p className="mt-2 text-xs text-slate-500">Feedback is calibrated differently for youth and older players.</p>
                </div>

                <div className="mt-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-400">Tone</p>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {TONE_OPTIONS.map((option) => {
                      const active = tone === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setTone(option.value)}
                          className={`rounded-2xl border px-4 py-3 text-left transition ${
                            active
                              ? 'border-emerald-300/40 bg-emerald-300/10 text-white'
                              : 'border-white/10 bg-white/5 text-slate-300 hover:border-white/20 hover:text-white'
                          }`}
                        >
                          <div className="text-lg">{option.icon}</div>
                          <div className="mt-1 text-sm font-medium">{option.label}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  <button
                    onClick={() => {
                      if (!selectedVideo || isAnalyzing) return;
                      const hasConsented = localStorage.getItem('fieldhouse_ai_consent') === 'true';
                      if (hasConsented) {
                        startAnalysis();
                      } else {
                        setShowAiConsent(true);
                      }
                    }}
                    disabled={!selectedVideo || isAnalyzing}
                    className="inline-flex items-center justify-center rounded-full bg-emerald-300 px-4 py-3 text-sm font-semibold text-slate-950 transition hover:bg-emerald-200 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isAnalyzing ? (
                      <>
                        <ArrowPathIcon className="mr-2 h-4 w-4 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <SparklesIcon className="mr-2 h-4 w-4" />
                        Start AI Analysis
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={resetAnalysis}
                    className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium text-slate-200 transition hover:border-white/20 hover:bg-white/10 hover:text-white"
                  >
                    Reset
                  </button>
                </div>

                {isAnalyzing && (
                  <div className="mt-5 rounded-2xl border border-white/10 bg-slate-950/70 p-4">
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="font-medium text-white">{ANALYSIS_STEPS[Math.max(0, currentStep - 1)] || ANALYSIS_STEPS[0]}</span>
                      <span className="text-slate-400">{Math.round((currentStep / ANALYSIS_STEPS.length) * 100)}%</span>
                    </div>
                    <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-800">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-emerald-300 via-sky-300 to-orange-300 transition-all duration-300"
                        style={{ width: `${(currentStep / ANALYSIS_STEPS.length) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </section>
            </div>

            <div className="space-y-6">
              <section className="rounded-[28px] border border-white/10 bg-slate-900/80 p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.32em] text-slate-400">Latest Swing</p>
                <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1.25fr)_280px]">
                  <div className="overflow-hidden rounded-[26px] border border-white/10 bg-slate-950/80">
                    {comparisonFrameSrc ? (
                      <img
                        src={comparisonFrameSrc}
                        alt="Latest analysis frame"
                        className="h-full min-h-[320px] w-full object-cover"
                        onError={(event) => {
                          event.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : selectedVideo?.previewUrl ? (
                      <video
                        src={selectedVideo.previewUrl}
                        controls
                        playsInline
                        className="h-full min-h-[320px] w-full object-cover"
                        onLoadedMetadata={(event) => {
                          const duration = event.currentTarget.duration;
                          const minutes = Math.floor(duration / 60);
                          const seconds = Math.floor(duration % 60);
                          setSelectedVideo((previous) => ({
                            ...previous,
                            duration: `${minutes}:${seconds.toString().padStart(2, '0')}`,
                          }));
                        }}
                      />
                    ) : (
                      <div className="flex min-h-[320px] flex-col items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(52,211,153,0.08),transparent_45%)] px-6 text-center">
                        <VideoCameraIcon className="h-12 w-12 text-slate-600" />
                        <p className="mt-4 text-base font-medium text-white">No clip loaded</p>
                        <p className="mt-2 max-w-sm text-sm leading-6 text-slate-400">
                          Upload a swing video to populate this panel with the current clip or the chosen comparison frame.
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="grid gap-5">
                    <div className="rounded-[24px] border border-white/10 bg-slate-950/70 p-5">
                      <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-400">Practice Focus</p>
                      <h3 className="mt-4 text-2xl font-semibold leading-tight text-white">{practiceFocus.title}</h3>
                      <p className="mt-3 text-sm leading-6 text-slate-300">{practiceFocus.detail}</p>
                      <div className={`mt-4 text-sm font-semibold ${focusTone.className}`}>{analysis ? focusTone.label : 'Waiting for clip'}</div>
                    </div>

                    <div className="rounded-[24px] border border-white/10 bg-slate-950/70 p-5">
                      <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-400">Clip Controls</p>
                      <div className="mt-5 grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="flex min-h-[88px] items-center justify-center rounded-full bg-white text-slate-950 transition hover:bg-slate-200"
                        >
                          <ArrowUpTrayIcon className="h-7 w-7" />
                        </button>
                        <button
                          type="button"
                          onClick={selectedVideo?.file ? startAnalysis : () => setShowHistory(true)}
                          disabled={isAnalyzing}
                          className="flex min-h-[88px] items-center justify-center rounded-full bg-white text-slate-950 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {selectedVideo?.file ? (
                            <SparklesIcon className="h-7 w-7" />
                          ) : (
                            <ClockIcon className="h-7 w-7" />
                          )}
                        </button>
                      </div>
                      <div className="mt-5 text-sm text-slate-400">
                        <div className="flex items-center justify-between gap-3">
                          <span>Type</span>
                          <span className="text-white">{getAnalysisTypeLabel(analysis?.type || analysisType)}</span>
                        </div>
                        <div className="mt-2 flex items-center justify-between gap-3">
                          <span>Updated</span>
                          <span className="text-white">{formatDisplayDate(latestPreviewDate)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {analysis?.error ? (
                <section className="rounded-[28px] border border-rose-400/20 bg-rose-400/10 p-5">
                  <div className="flex items-start gap-4">
                    <ExclamationTriangleIcon className="mt-0.5 h-7 w-7 text-rose-200" />
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.28em] text-rose-200/80">Analysis Failed</p>
                      <p className="mt-3 max-w-2xl text-sm leading-6 text-rose-50">{analysis.errorMessage}</p>
                      {analysis.insights.recommendations?.length > 0 && (
                        <div className="mt-4 grid gap-2">
                          {analysis.insights.recommendations.map((recommendation, index) => (
                            <div key={`${recommendation}-${index}`} className="rounded-2xl border border-rose-300/15 bg-black/10 px-4 py-3 text-sm text-rose-50">
                              {recommendation}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              ) : (
                <>
                  {analysis?.insights?.professionalComparison && (
                    <section className="rounded-[24px] border border-violet-400/20 bg-gradient-to-br from-violet-400/10 to-slate-900/60 p-5">
                      <div className="flex items-center gap-2">
                        <UserGroupIcon className="h-4 w-4 text-violet-300" />
                        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-violet-200/80">Pro Comparison</p>
                      </div>
                      <p className="mt-3 text-lg font-semibold leading-snug text-white">
                        {buildComparisonLine(analysis.insights.professionalComparison, analysis.type || analysisType)}
                      </p>
                      <div className="mt-3 flex flex-wrap items-center gap-3">
                        <span className="text-sm font-medium text-violet-100">{analysis.insights.professionalComparison.player}</span>
                        {formatSimilarity(analysis.insights.professionalComparison.similarity) != null && (
                          <span className="rounded-full bg-white/8 px-2.5 py-1 text-xs text-slate-300">
                            {formatSimilarity(analysis.insights.professionalComparison.similarity)}% match
                          </span>
                        )}
                      </div>
                      {analysis.insights.professionalComparison.notes && (
                        <p className="mt-2 text-sm leading-6 text-violet-100/70">
                          {analysis.insights.professionalComparison.notes}
                        </p>
                      )}
                    </section>
                  )}

                  <section className="grid gap-4 lg:grid-cols-3">
                    <div className="rounded-[24px] border border-emerald-400/15 bg-emerald-400/10 p-5">
                      <p className="text-xs font-semibold uppercase tracking-[0.28em] text-emerald-100/70">What's Working</p>
                      <ul className="mt-4 space-y-2.5">
                        {normalizeArray(analysis?.insights?.strengths).slice(0, 3).length > 0 ? (
                          normalizeArray(analysis?.insights?.strengths).slice(0, 3).map((strength, index) => (
                            <li key={`${strength}-${index}`} className="flex items-start gap-2.5 text-sm text-emerald-50">
                              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-300" />
                              {strength}
                            </li>
                          ))
                        ) : (
                          <li className="text-sm text-emerald-50/80">Strength callouts appear after the clip is processed.</li>
                        )}
                      </ul>
                    </div>

                    <div className="rounded-[24px] border border-amber-400/15 bg-amber-400/10 p-5">
                      <p className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-100/70">Work On</p>
                      <ul className="mt-4 space-y-2.5">
                        {normalizeArray(analysis?.insights?.improvements).slice(0, 3).length > 0 ? (
                          normalizeArray(analysis?.insights?.improvements).slice(0, 3).map((improvement, index) => (
                            <li key={`${improvement}-${index}`} className="flex items-start gap-2.5 text-sm text-amber-50">
                              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-300" />
                              {improvement}
                            </li>
                          ))
                        ) : (
                          <li className="text-sm text-amber-50/80">Corrective notes appear after the model evaluates the clip.</li>
                        )}
                      </ul>
                    </div>

                    <div className="rounded-[24px] border border-sky-400/15 bg-sky-400/10 p-5">
                      <p className="text-xs font-semibold uppercase tracking-[0.28em] text-sky-100/70">Drills</p>
                      <ul className="mt-4 space-y-2.5">
                        {normalizeRecommendations(analysis?.insights?.recommendations).slice(0, 3).length > 0 ? (
                          normalizeRecommendations(analysis?.insights?.recommendations).slice(0, 3).map((item, index) => (
                            <li key={`${item}-${index}`} className="flex items-start gap-2.5 text-sm text-sky-50">
                              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-300" />
                              {item}
                            </li>
                          ))
                        ) : (
                          <li className="text-sm text-sky-50/80">Drill recommendations appear once an analysis is available.</li>
                        )}
                      </ul>
                    </div>
                  </section>

                  {(topMetrics.length > 0 || normalizeKeyMoments(analysis?.insights?.keyMoments).length > 0) && (
                    <section className="grid gap-6 xl:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
                      <div className="rounded-[24px] border border-white/10 bg-slate-900/75 p-5">
                        <div className="flex items-center gap-2">
                          <ChartBarIcon className="h-5 w-5 text-slate-300" />
                          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-slate-300">Metrics</p>
                        </div>
                        {topMetrics.length > 0 ? (
                          <div className="mt-5 grid grid-cols-2 gap-3">
                            {topMetrics.map(([key, value]) => (
                              <div key={key} className="rounded-2xl border border-white/10 bg-slate-950/70 p-4">
                                <div className="text-xl font-semibold text-white">{String(value)}</div>
                                <div className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-500">
                                  {formatMetricLabel(key)}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="mt-4 text-sm text-slate-400">No numeric metrics were returned for this clip.</p>
                        )}
                      </div>

                      <div className="rounded-[24px] border border-white/10 bg-slate-900/75 p-5">
                        <div className="flex items-center gap-2">
                          <ClockIcon className="h-5 w-5 text-slate-300" />
                          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-slate-300">Key Moments</p>
                        </div>
                        {normalizeKeyMoments(analysis?.insights?.keyMoments).length > 0 ? (
                          <div className="mt-5 grid gap-3">
                            {normalizeKeyMoments(analysis?.insights?.keyMoments).map((moment, index) => (
                              <div key={`${moment.timestamp || 'moment'}-${index}`} className="rounded-2xl border border-white/10 bg-slate-950/70 p-4">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <span className="text-sm font-medium text-white">{moment.timestamp || 'Key frame'}</span>
                                  <span className="rounded-full bg-white/5 px-2.5 py-1 text-[11px] uppercase tracking-[0.18em] text-slate-300">
                                    {moment.importance || 'note'}
                                  </span>
                                </div>
                                <p className="mt-3 text-sm leading-6 text-slate-300">{moment.description}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="mt-4 text-sm text-slate-400">No timestamped moments were returned for this clip.</p>
                        )}
                      </div>
                    </section>
                  )}

                  {analysis?.insights?.mediaPipeData && (
                    <section className="rounded-[24px] border border-sky-400/15 bg-sky-400/10 p-5">
                      <div className="flex items-center gap-2">
                        <SparklesIcon className="h-5 w-5 text-sky-100" />
                        <p className="text-sm font-semibold uppercase tracking-[0.24em] text-sky-100">AI Motion Analysis</p>
                      </div>
                      <div className="mt-5 grid gap-3 md:grid-cols-3">
                        <div className="rounded-2xl border border-sky-200/15 bg-black/10 p-4">
                          <div className="text-2xl font-semibold text-white">
                            {(analysis.insights.mediaPipeData.poseConfidence * 100).toFixed(0)}%
                          </div>
                          <div className="mt-1 text-xs uppercase tracking-[0.18em] text-sky-100/70">Pose Confidence</div>
                        </div>
                        <div className="rounded-2xl border border-sky-200/15 bg-black/10 p-4">
                          <div className="text-2xl font-semibold text-white">
                            {analysis.insights.mediaPipeData.keypointsDetected}/33
                          </div>
                          <div className="mt-1 text-xs uppercase tracking-[0.18em] text-sky-100/70">Keypoints</div>
                        </div>
                        <div className="rounded-2xl border border-sky-200/15 bg-black/10 p-4">
                          <div className="text-sm font-medium text-white">Movement Analysis</div>
                          <div className="mt-2 text-sm leading-6 text-sky-50/85">
                            {analysis.insights.mediaPipeData.movementAnalysis}
                          </div>
                        </div>
                      </div>
                    </section>
                  )}

                  {analysis?.insights?.annotatedFrames?.length > 0 && (
                    <section className="rounded-[24px] border border-white/10 bg-slate-900/75 p-5">
                      <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-400">Pose Frames</p>
                      <p className="mt-1 text-xs text-slate-500">Key frames extracted from your clip with MediaPipe pose overlay</p>

                      {/* Large selected frame */}
                      {(() => {
                        const frame = analysis.insights.annotatedFrames[selectedFrameIndex];
                        if (!frame?.png_b64) return null;
                        // JPEG b64 starts with /9j; PNG starts with iVBOR
                        const mime = frame.png_b64.startsWith('/9j') ? 'image/jpeg' : 'image/png';
                        return (
                          <div className="mt-4 overflow-hidden rounded-[20px] border border-white/10 bg-slate-950">
                            <img
                              src={`data:${mime};base64,${frame.png_b64}`}
                              alt={`Pose frame ${selectedFrameIndex + 1}`}
                              className="w-full object-contain max-h-[480px]"
                            />
                            <div className="flex items-center justify-between px-4 py-2 text-xs text-slate-400">
                              <span>Frame {selectedFrameIndex + 1} of {analysis.insights.annotatedFrames.length}</span>
                              {frame.timestamp_ms != null && (
                                <span>{(frame.timestamp_ms / 1000).toFixed(2)}s</span>
                              )}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Thumbnail strip */}
                      {analysis.insights.annotatedFrames.length > 1 && (
                        <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                          {analysis.insights.annotatedFrames.map((frame, idx) => {
                            if (!frame?.png_b64) return null;
                            const mime = frame.png_b64.startsWith('/9j') ? 'image/jpeg' : 'image/png';
                            return (
                              <button
                                key={frame.frame_index ?? idx}
                                type="button"
                                onClick={() => setSelectedFrameIndex(idx)}
                                className={`relative shrink-0 overflow-hidden rounded-xl border-2 transition ${
                                  idx === selectedFrameIndex
                                    ? 'border-emerald-300/70 ring-1 ring-emerald-300/40'
                                    : 'border-white/10 hover:border-white/30'
                                }`}
                              >
                                <img
                                  src={`data:${mime};base64,${frame.png_b64}`}
                                  alt={`Frame ${idx + 1}`}
                                  className="h-16 w-24 object-cover"
                                />
                                {frame.timestamp_ms != null && (
                                  <span className="absolute bottom-0 right-0 rounded-tl-lg bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
                                    {(frame.timestamp_ms / 1000).toFixed(1)}s
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </section>
                  )}

                  {analysis?.insights?.aiAnalysisText && (
                    <section className="rounded-[24px] border border-white/10 bg-slate-900/75 p-5">
                      <div className="flex items-center gap-2">
                        <SparklesIcon className="h-5 w-5 text-slate-300" />
                        <p className="text-sm font-semibold uppercase tracking-[0.24em] text-slate-300">Detailed AI Analysis</p>
                      </div>
                      <div className="mt-5 rounded-2xl border border-white/10 bg-slate-950/70 p-4 text-sm leading-7 text-slate-300 whitespace-pre-wrap">
                        {analysis.insights.aiAnalysisText}
                      </div>
                    </section>
                  )}

                  {/* Share with Coach */}
                  {!isCoach && (
                    <section className="rounded-[24px] border border-emerald-400/20 bg-emerald-400/5 p-5">
                      <div className="flex items-center gap-2 mb-4">
                        <PaperAirplaneIcon className="h-5 w-5 text-emerald-300" />
                        <p className="text-sm font-semibold uppercase tracking-[0.24em] text-emerald-200">Share with Coach</p>
                      </div>

                      {analysisShares.length > 0 ? (
                        <div className="space-y-3">
                          {analysisShares.map((share) => (
                            <div key={share.id} className="rounded-2xl border border-white/10 bg-slate-950/60 p-4">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-sm font-medium text-white">{share.Coach?.name || 'Coach'}</span>
                                <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${share.status === 'responded' ? 'bg-emerald-400/15 text-emerald-200' : 'bg-white/8 text-slate-400'}`}>
                                  {share.status === 'responded' ? 'Responded' : 'Pending'}
                                </span>
                              </div>
                              {share.status === 'responded' && (
                                <div className="mt-3 space-y-2">
                                  {share.coach_feedback && (
                                    <div className="rounded-xl bg-slate-800/60 p-3 text-sm text-slate-200">
                                      <p className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-1">Coach Feedback</p>
                                      {share.coach_feedback}
                                    </div>
                                  )}
                                  {Array.isArray(share.coach_drills) && share.coach_drills.length > 0 && (
                                    <div className="rounded-xl bg-sky-400/10 p-3">
                                      <p className="text-xs uppercase tracking-[0.18em] text-sky-300 mb-2">Assigned Drills</p>
                                      <ul className="space-y-1.5">
                                        {share.coach_drills.map((drill, i) => (
                                          <li key={i} className="flex items-start gap-2 text-sm text-sky-50">
                                            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-300" />
                                            {drill}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={handleShareWithCoach}
                            disabled={isSharing}
                            className="mt-1 text-xs text-emerald-300 hover:text-emerald-200 underline underline-offset-2"
                          >
                            Re-share updated analysis
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-sm text-slate-400">Send this analysis to your coach so they can review it and respond with personalized feedback and drills.</p>
                          <textarea
                            value={shareNote}
                            onChange={(e) => setShareNote(e.target.value)}
                            placeholder="Optional note for your coach… (e.g. 'I've been struggling with my hip rotation')"
                            rows={2}
                            className="w-full rounded-2xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-emerald-300/40 resize-none"
                          />
                          <button
                            type="button"
                            onClick={handleShareWithCoach}
                            disabled={isSharing}
                            className="inline-flex items-center rounded-full bg-emerald-300 px-5 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-emerald-200 disabled:opacity-50"
                          >
                            <PaperAirplaneIcon className="mr-2 h-4 w-4" />
                            {isSharing ? 'Sharing…' : 'Share with Coach'}
                          </button>
                        </div>
                      )}
                    </section>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Coach Inbox */}
          {isCoach && (
            <div className="mt-6 rounded-[28px] border border-white/10 bg-slate-900/80 p-5">
              <button
                type="button"
                onClick={() => { setShowCoachInbox(v => !v); if (!showCoachInbox) fetchCoachInbox(); }}
                className="flex w-full items-center justify-between gap-3"
              >
                <div className="flex items-center gap-2">
                  <UserGroupIcon className="h-5 w-5 text-emerald-300" />
                  <p className="text-sm font-semibold uppercase tracking-[0.28em] text-emerald-200">Player Analysis Inbox</p>
                  {coachInbox.filter(s => s.status === 'pending').length > 0 && (
                    <span className="rounded-full bg-emerald-400 px-2 py-0.5 text-xs font-semibold text-slate-950">
                      {coachInbox.filter(s => s.status === 'pending').length}
                    </span>
                  )}
                </div>
                <span className="text-xs text-slate-400">{showCoachInbox ? 'Collapse' : 'Expand'}</span>
              </button>

              {showCoachInbox && (
                <div className="mt-5 space-y-4">
                  {coachInbox.length === 0 ? (
                    <p className="text-sm text-slate-400">No players have shared analyses with you yet.</p>
                  ) : coachInbox.map((share) => (
                    <div key={share.id} className="rounded-[20px] border border-white/10 bg-slate-950/60 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-white">{share.Player?.name || 'Player'}</p>
                          <p className="text-xs text-slate-400 mt-0.5">
                            {share.Video?.analysisType || 'Analysis'} · {new Date(share.shared_at).toLocaleDateString()}
                          </p>
                          {share.player_note && (
                            <p className="mt-2 text-sm text-slate-300 italic">"{share.player_note}"</p>
                          )}
                        </div>
                        <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${share.status === 'responded' ? 'bg-emerald-400/15 text-emerald-200' : 'bg-amber-400/15 text-amber-200'}`}>
                          {share.status === 'responded' ? 'Done' : 'Needs response'}
                        </span>
                      </div>

                      {/* AI analysis summary */}
                      {share.Video?.analysisData && (() => {
                        const ad = share.Video.analysisData.analysis || share.Video.analysisData;
                        const score = ad.overallScore;
                        const focus = normalizeArray(ad.improvements)[0] || normalizeRecommendations(ad.recommendations)[0];
                        return score || focus ? (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {score != null && <span className="rounded-full bg-white/8 px-3 py-1 text-xs text-slate-300">Score {clampScore(score)}</span>}
                            {focus && <span className="rounded-full bg-white/8 px-3 py-1 text-xs text-slate-300">{truncateText(focus, 60)}</span>}
                          </div>
                        ) : null;
                      })()}

                      {share.status === 'responded' && (
                        <div className="mt-3 rounded-xl bg-slate-800/40 p-3">
                          <p className="text-xs text-slate-400 mb-1">Your response</p>
                          {share.coach_feedback && <p className="text-sm text-slate-200">{share.coach_feedback}</p>}
                          {Array.isArray(share.coach_drills) && share.coach_drills.length > 0 && (
                            <ul className="mt-2 space-y-1">
                              {share.coach_drills.map((d, i) => <li key={i} className="text-xs text-sky-200">• {d}</li>)}
                            </ul>
                          )}
                        </div>
                      )}

                      {respondingTo === share.id ? (
                        <div className="mt-4 space-y-3">
                          <textarea
                            value={coachFeedback}
                            onChange={(e) => setCoachFeedback(e.target.value)}
                            placeholder="Write your feedback for this player…"
                            rows={3}
                            className="w-full rounded-2xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-emerald-300/40 resize-none"
                          />
                          <textarea
                            value={coachDrills}
                            onChange={(e) => setCoachDrills(e.target.value)}
                            placeholder="Drills (one per line)…"
                            rows={2}
                            className="w-full rounded-2xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-emerald-300/40 resize-none"
                          />
                          <div className="flex gap-3">
                            <button
                              type="button"
                              onClick={() => handleCoachRespond(share.id)}
                              disabled={isResponding || !coachFeedback.trim()}
                              className="inline-flex items-center rounded-full bg-emerald-300 px-5 py-2 text-sm font-semibold text-slate-950 transition hover:bg-emerald-200 disabled:opacity-50"
                            >
                              <CheckCircleIcon className="mr-2 h-4 w-4" />
                              {isResponding ? 'Sending…' : 'Send Feedback'}
                            </button>
                            <button
                              type="button"
                              onClick={() => { setRespondingTo(null); setCoachFeedback(''); setCoachDrills(''); }}
                              className="rounded-full border border-white/10 px-4 py-2 text-sm text-slate-300 hover:text-white"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setRespondingTo(share.id);
                            setCoachFeedback(share.coach_feedback || '');
                            setCoachDrills(Array.isArray(share.coach_drills) ? share.coach_drills.join('\n') : '');
                          }}
                          className="mt-3 inline-flex items-center rounded-full border border-emerald-300/30 px-4 py-2 text-sm text-emerald-200 hover:border-emerald-300/60 transition"
                        >
                          {share.status === 'responded' ? 'Edit Response' : 'Respond'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* AI Data Consent Modal */}
      {showAiConsent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-slate-900 p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <SparklesIcon className="h-6 w-6 text-emerald-300 shrink-0" />
              <h2 className="text-lg font-semibold text-white">AI Analysis — Data Notice</h2>
            </div>
            <p className="text-sm leading-6 text-slate-300 mb-3">
              To generate your coaching analysis, FieldHouse will send frames extracted from your video to <strong className="text-white">OpenAI</strong> (openai.com).
            </p>
            <ul className="text-sm text-slate-300 space-y-1.5 mb-4 list-disc pl-5">
              <li>Frames are used only to produce your analysis report.</li>
              <li>OpenAI does not retain frames for model training under our API agreement.</li>
              <li>No other personal information is included in the request.</li>
            </ul>
            <p className="text-sm text-slate-400 mb-5">
              You can review our full privacy policy at <span className="text-emerald-300">fieldhouse.club/privacy</span>.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowAiConsent(false)}
                className="flex-1 rounded-full border border-white/10 py-2.5 text-sm font-medium text-slate-300 hover:text-white transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  localStorage.setItem('fieldhouse_ai_consent', 'true');
                  setShowAiConsent(false);
                  startAnalysis();
                }}
                className="flex-1 rounded-full bg-emerald-300 py-2.5 text-sm font-semibold text-slate-950 hover:bg-emerald-200 transition"
              >
                I Agree — Analyze
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoAnalysis;
